'use client'

/**
 * Shot triggers (data-taste buttons) exist so take-route-shots can force the
 * error, success, sent and hours-empty states. They are opacity 0 but were
 * still in the tab order and the accessibility tree, so a keyboard or
 * screen-reader visitor could reach "Show sent" and see "Message received"
 * on an empty form (UX review 2026-10-15, F01).
 *
 * Render them only for an automated browser (navigator.webdriver, the same
 * automation signal lib/analytics/automation.ts uses). Playwright sets it, so
 * the shot tool keeps working; a person's browser never does.
 */
import { useEffect, useState } from 'react'

export function isAutomatedBrowser(nav: { webdriver?: boolean } | undefined): boolean {
  return nav?.webdriver === true
}

/**
 * Never in production (G17, 2026-10-20): Measurement's headless check saw the
 * buttons on live /contact because it is an automated browser. Only local dev
 * and Vercel preview builds render them. Fails closed when the env is unknown.
 */
export function shotTriggersAllowedInEnv(env: { nodeEnv?: string; vercelEnv?: string }): boolean {
  return env.nodeEnv === 'development' || env.vercelEnv === 'preview'
}

export function useShotTriggers(): boolean {
  const [on, setOn] = useState(false)
  useEffect(() => {
    if (!shotTriggersAllowedInEnv({ nodeEnv: process.env.NODE_ENV, vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV })) return
    setOn(isAutomatedBrowser(typeof navigator === 'undefined' ? undefined : navigator))
  }, [])
  return on
}
